import { Code, ConnectError } from '@bufbuild/connect';
import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { isEqual } from 'lodash-es';
import { prepend } from 'ramda';
import { FC, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  CreatePatientReq,
  SetPatientPersonalDataReq,
} from '@/shared/api/protocol-ts/api/core/svc_patient_pb';
import { Gender } from '@/shared/api/protocol-ts/model/dto_base_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { UserBadRequestError } from '@/shared/config';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { gender } from '@/shared/i18n';
import {
  formatDateToYYYYMMDD,
  getMaxDateOfBirthForMinAge,
  MINIMUM_PATIENT_AGE_FDA,
  MINIMUM_PATIENT_AGE_FDA_VERSION2,
  normalizeEmail,
  parseDateAsLocal,
  safeJSONParse,
} from '@/shared/lib';
import {
  Button,
  DatePicker,
  ErrorText,
  Icon,
  Input,
  InputLabel,
  Modal,
  RadioGroup,
} from '@/shared/ui';

import { DoctorsSelect } from '@/entities/doctors';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import { userModel } from '@/entities/user';

import {
  useFDA3DGPRestrictions,
  useFDAEnvironment,
} from '@/features/fdaRestrictions';
import {
  treatingDoctorsModel,
  TreatingDoctorsSelectDrawer,
} from '@/features/treatingDoctors';

import {
  EditPatientPayload,
  getPatientValidationSchema,
  NewPatientPayload,
  PATIENT_FORM_ID,
} from '../../config';
import { PatientRemovalConfirmationModal } from '../PatientRemovalConfirmationModal/PatientRemovalConfirmationModal';

import styles from './PatientModalForm.module.scss';

type PatientPayload = NewPatientPayload | EditPatientPayload;

export const PatientModalForm: FC = () => {
  const [globalFormError, setGlobalFormError] = useState<string>();

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { isMobile } = useMedia();

  const {
    visible,
    data: { patientID },
  } = useAppSelector(modalModel.selectors.selectPatientModalForm);
  const patient = useAppSelector(
    patientModel.selectors.selectPatientByID(patientID),
  );
  const patientList = useAppSelector(patientModel.selectors.selectAll);
  const user = useAppSelector(userModel.selectors.selectCurrentUser);
  const locale = useAppSelector(userModel.selectors.selectUserLocale);
  const currentOrganizationID = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const doctorsOptions = useAppSelector(
    treatingDoctorsModel.selectors.selectOrganizationDoctorsOptions,
  );

  const isForbidPatientEditing = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Forbid_PatientEditing,
    ),
  );

  const { shouldEnforceMinimumAge } = useFDA3DGPRestrictions();
  const { isFDAVersion2 } = useFDAEnvironment();

  // FDA 2.0 accepts patients from 3 years old, while the legacy FDA 3D GP
  // clearance keeps the 22-year restriction
  const minimumPatientAge = isFDAVersion2
    ? MINIMUM_PATIENT_AGE_FDA_VERSION2
    : shouldEnforceMinimumAge
      ? MINIMUM_PATIENT_AGE_FDA
      : undefined;

  const isEditPatient = Boolean(patientID);
  const patientPersonalData = patient?.PersonalData;
  const { CanDeletePatient = false } = patient?.YourPermissions ?? {};

  const dateOfBirth = useMemo(
    () =>
      patient?.PersonalData?.DateOfBirth &&
      patient.PersonalData.DateOfBirth.length > 0
        ? parseDateAsLocal(patient.PersonalData.DateOfBirth)
        : new Date(),
    [patient?.PersonalData?.DateOfBirth],
  );

  const maxDateOfBirth = useMemo(
    () =>
      minimumPatientAge !== undefined
        ? getMaxDateOfBirthForMinAge(minimumPatientAge)
        : new Date(),
    [minimumPatientAge],
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isSubmitted },
    reset,
    setValue,
    setFocus,
  } = useForm<PatientPayload>({
    resolver: yupResolver(
      getPatientValidationSchema({
        validateDoctors: !isEditPatient,
        minimumAge: minimumPatientAge,
      }),
    ),
    mode: 'all',
    shouldFocusError: true,
  });

  const onClose = () => {
    setGlobalFormError('');
    reset({});
    dispatch(modalModel.actions.closeModal(ModalID.PatientModalForm));
  };

  const onSubmit: SubmitHandler<PatientPayload> = async (data) => {
    const formattedDate = formatDateToYYYYMMDD(data.dateOfBirth);
    const normalizedEmail = data.email ? normalizeEmail(data.email) : '';

    try {
      if (isEditPatient) {
        const emailFromData = normalizedEmail ? [normalizedEmail] : [];
        const shouldUpdatePersonalData =
          data.firstName !== patient?.PersonalData?.FirstName ||
          data.lastName !== patient?.PersonalData?.LastName ||
          !isEqual(emailFromData, patient?.PersonalData?.Emails) ||
          formattedDate !== patient?.PersonalData?.DateOfBirth;
        const shouldUpdateGender = data.gender !== patient?.Gender;
        const shouldUpdateExternalID =
          data.patientExternalID !== patient?.ExternalID;

        if (shouldUpdatePersonalData) {
          const updatedPatient = await dispatch(
            patientModel.thunks.setPatientPersonalData(
              new SetPatientPersonalDataReq({
                PatientID: patientID,
                PersonalData: {
                  FirstName: data.firstName,
                  LastName: data.lastName,
                  DateOfBirth: formattedDate,
                  Emails: emailFromData,
                  Phones: [],
                },
              }),
            ),
          ).unwrap();

          if (updatedPatient.Patient) {
            dispatch(patientModel.actions.setNewestOne(updatedPatient.Patient));
          }
        }

        if (shouldUpdateGender) {
          const updatedPatient = await dispatch(
            patientModel.thunks.setPatientGender({
              PatientID: patientID,
              Gender: data.gender,
            }),
          ).unwrap();

          if (updatedPatient.Patient) {
            dispatch(patientModel.actions.setNewestOne(updatedPatient.Patient));
          }
        }

        if (shouldUpdateExternalID) {
          const updatedPatient = await dispatch(
            patientModel.thunks.setPatientExternalID({
              PatientID: patientID,
              ExternalID: data.patientExternalID || '',
            }),
          ).unwrap();

          if (updatedPatient.Patient) {
            dispatch(patientModel.actions.setNewestOne(updatedPatient.Patient));
          }
        }
        // TODO: To add or remove doctors using AddPatientTeamMember and RevokePatientTeamAccess from svc_access
        // Need to make access entity
      } else {
        const newPatient = await dispatch(
          patientModel.thunks.createPatient(
            new CreatePatientReq({
              Attributes: [],
              OrganizationID: currentOrganizationID,
              Gender: data.gender,
              PersonalData: {
                FirstName: data.firstName,
                LastName: data.lastName,
                DateOfBirth: formattedDate,
                Emails: normalizedEmail ? [normalizedEmail] : [],
                Phones: [],
              },
              ExternalID: data.patientExternalID || '',
              TreatingStaffIDs: data.treatingDoctors || [],
            }),
          ),
        ).unwrap();

        if (newPatient.Patient) {
          const newPatientList = prepend(newPatient.Patient, patientList);
          dispatch(patientModel.actions.setMany(newPatientList));
        }
      }

      setGlobalFormError(undefined);
      onClose();
    } catch (error) {
      const connectError = ConnectError.from(error);
      const parsedMessage = safeJSONParse(connectError?.rawMessage);

      if (
        connectError.code === Code.FailedPrecondition &&
        parsedMessage?.code ===
          UserBadRequestError.NON_UNIQUE_PATIENT_EXTERNAL_ID
      ) {
        setGlobalFormError(
          formatMessage({
            id: 'addNewPatient.error.uniqueID',
            defaultMessage:
              'This patient ID is already in use. Please choose a different one.',
          }),
        );
      } else {
        setGlobalFormError(connectError.message);
      }
    }
  };

  const handlePatientRemove = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.PatientRemovalConfirmationModal,
        data: {
          patientID,
        },
      }),
    );
  };

  // Set default
  useEffect(() => {
    if (patient) {
      reset({
        firstName: patientPersonalData?.FirstName,
        lastName: patientPersonalData?.LastName,
        email: patientPersonalData?.Emails?.at(0),
        patientExternalID: patient?.ExternalID,
        dateOfBirth,
        gender: patient?.Gender,
      });
    }
  }, [
    patientPersonalData,
    patient?.ExternalID,
    patient?.Gender,
    patient,
    reset,
    dateOfBirth,
  ]);

  useEffect(() => {
    if (!patientID && !!doctorsOptions.length) {
      const currentUserOption = doctorsOptions.find(
        (option) => option.userID === user.ID,
      );
      const defaultOption =
        currentUserOption ??
        (doctorsOptions.length === 1 ? doctorsOptions[0] : undefined);
      const treatingDoctorsDefaultValue = defaultOption
        ? [defaultOption.value]
        : [];

      setValue('treatingDoctors', treatingDoctorsDefaultValue);
    }
  }, [doctorsOptions, user]);

  useEffect(() => {
    if (visible) {
      // Small delay to ensure the modal is fully rendered
      const timer = setTimeout(() => {
        setFocus('firstName');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [visible, setFocus]);

  // If patient editing is forbidden, don't render the modal
  if (isForbidPatientEditing) {
    return null;
  }

  return (
    <Modal
      data-component="patient-form-modal"
      containerClassName={styles.container}
      bodyClassName={styles.modalBody}
      title={
        isEditPatient ? (
          <FormattedMessage
            id="patientModalForm.editTitle"
            defaultMessage="Edit patient information"
          />
        ) : (
          <FormattedMessage
            id="patientModalForm.title"
            defaultMessage="New patient"
          />
        )
      }
      isOpen={visible}
      onCancel={onClose}
      borderless={isMobile}
      footer={
        <>
          {isEditPatient && CanDeletePatient && !isMobile && (
            <Button
              className={styles.removeButton}
              variant="secondary"
              icon="delete"
              danger
              onClick={handlePatientRemove}
            >
              <FormattedMessage id="global.remove" defaultMessage="Remove" />
            </Button>
          )}

          {!isMobile && (
            <Button size="large" variant="tertiary" onClick={onClose}>
              <FormattedMessage id="global.cancel" defaultMessage="Cancel" />
            </Button>
          )}

          <Button
            size="large"
            type="submit"
            form={PATIENT_FORM_ID}
            loading={isSubmitting}
          >
            {isEditPatient ? (
              <FormattedMessage
                id="patientModalForm.saveButton"
                defaultMessage="Save"
              />
            ) : (
              <FormattedMessage id="global.add" defaultMessage="Add" />
            )}
          </Button>
        </>
      }
    >
      <form
        id={PATIENT_FORM_ID}
        onSubmit={handleSubmit(onSubmit)}
        className={styles.form}
      >
        <div className={styles.formRow}>
          <Controller
            control={control}
            name="firstName"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                ref={ref}
                value={value}
                name={name}
                onBlur={() => {
                  if (isSubmitted) {
                    onBlur();
                  }
                }}
                onChange={(event) => {
                  if (!isSubmitted) {
                    setValue(name, event.target.value);
                  } else {
                    onChange(event);
                  }
                }}
                label={
                  <FormattedMessage
                    id="patientModalForm.label.firstName"
                    defaultMessage="First Name"
                  />
                }
                error={error?.message}
                required
              />
            )}
          />

          <Controller
            control={control}
            name="lastName"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                ref={ref}
                value={value}
                name={name}
                onBlur={() => {
                  if (isSubmitted) {
                    onBlur();
                  }
                }}
                onChange={(event) => {
                  if (!isSubmitted) {
                    setValue(name, event.target.value);
                  } else {
                    onChange(event);
                  }
                }}
                label={
                  <FormattedMessage
                    id="patientModalForm.label.lastName"
                    defaultMessage="Last Name"
                  />
                }
                error={error?.message}
                required
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name="email"
          render={({
            field: { ref, value, name, onBlur, onChange },
            fieldState: { error, isTouched },
          }) => (
            <Input
              ref={ref}
              value={value}
              name={name}
              onBlur={onBlur}
              onChange={(event) => {
                if (!isTouched) {
                  setValue(name, event.target.value);
                } else {
                  onChange(event);
                }
              }}
              label={
                <FormattedMessage
                  id="patientModalForm.label.email"
                  defaultMessage="Email"
                />
              }
              error={error?.message}
            />
          )}
        />

        <div className={styles.formRow}>
          <Controller
            control={control}
            name="dateOfBirth"
            render={({
              field: { name, value, onChange },
              fieldState: { error },
            }) =>
              isMobile ? (
                <Input
                  name={name}
                  type="date"
                  inputMode="numeric"
                  value={formatDateToYYYYMMDD(value)}
                  max={formatDateToYYYYMMDD(maxDateOfBirth)}
                  onChange={(event) => {
                    onChange(new Date(event.target.value));
                  }}
                  label={
                    <FormattedMessage
                      id="patientModalForm.label.dateOfBirth"
                      defaultMessage="Date of birth"
                    />
                  }
                  error={error?.message}
                  required
                />
              ) : (
                <DatePicker
                  locale={locale}
                  value={value}
                  maxDate={maxDateOfBirth}
                  onChange={onChange}
                  placeholder={formatMessage({
                    id: 'patientModalForm.placeholder.selectDate',
                    defaultMessage: 'Select date',
                  })}
                  label={
                    <FormattedMessage
                      id="patientModalForm.label.dateOfBirth"
                      defaultMessage="Date of birth"
                    />
                  }
                  error={error?.message}
                  required
                />
              )
            }
          />

          <Controller
            control={control}
            name="patientExternalID"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                ref={ref}
                value={value}
                name={name}
                onBlur={() => {
                  if (isSubmitted) {
                    onBlur();
                  }
                }}
                onChange={(event) => {
                  if (!isSubmitted) {
                    setValue(name, event.target.value);
                  } else {
                    onChange(event);
                  }
                }}
                label={
                  <FormattedMessage
                    id="patientModalForm.label.patientID"
                    defaultMessage="Patient ID"
                  />
                }
                error={error?.message}
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name="gender"
          render={({
            field: { ref, value, name, onChange, onBlur },
            fieldState: { error },
          }) => (
            <div className={styles.radioGroupContainer}>
              <InputLabel
                label={formatMessage({
                  id: 'global.gender',
                  defaultMessage: 'Gender',
                })}
                required
              />
              <RadioGroup
                ref={ref}
                className={styles.radioGroup}
                value={value}
                name={name}
                variant="tab"
                onBlur={onBlur}
                onChange={onChange}
                error={error?.message}
                items={[
                  {
                    value: Gender.GenderMale,
                    label: formatMessage(gender[Gender.GenderMale]),
                  },
                  {
                    value: Gender.GenderFemale,
                    label: formatMessage(gender[Gender.GenderFemale]),
                  },
                  {
                    value: Gender.GenderOther,
                    label: formatMessage(gender[Gender.GenderOther]),
                  },
                ]}
              />
            </div>
          )}
        />

        {!isEditPatient && (
          <Controller
            control={control}
            name="treatingDoctors"
            render={({
              field: { value = [], onChange },
              fieldState: { error },
            }) =>
              isMobile ? (
                <>
                  {/* TODO: Make reusable component if there is more such selector triggers on mobile version */}
                  <button
                    type="button"
                    className={styles.mobileSelectButton}
                    onClick={() =>
                      dispatch(
                        modalModel.actions.openModal({
                          modalID: ModalID.TreatingDoctorsSelectDrawer,
                        }),
                      )
                    }
                  >
                    <InputLabel
                      className={styles.mobileSelectButtonLabel}
                      label={formatMessage({
                        id: 'doctorsSelect.label',
                        defaultMessage: 'Treating doctor',
                      })}
                      required
                    />
                    <span className={cn(styles.mobileSelectButtonList, 'p1m')}>
                      {doctorsOptions
                        .filter((doctor) => value?.includes(doctor.value))
                        .map((doctor) => doctor.label)
                        .join(', ') ||
                        formatMessage({
                          id: 'doctorsSelect.placeholder',
                          defaultMessage: 'Select doctor',
                        })}
                    </span>
                    <Icon
                      className={styles.mobileSelectButtonIcon}
                      size={24}
                      name="arrowDown"
                    />
                  </button>
                  <TreatingDoctorsSelectDrawer
                    doctorIDs={value as string[]}
                    onSave={(doctorsIDs) => onChange(doctorsIDs)}
                  />
                </>
              ) : (
                <DoctorsSelect
                  options={doctorsOptions}
                  value={value}
                  onChange={onChange}
                  error={error?.message}
                />
              )
            }
          />
        )}

        {isEditPatient && CanDeletePatient && isMobile && (
          <Button
            className={styles.mobileRemoveButton}
            variant="tertiary"
            danger
            onClick={handlePatientRemove}
          >
            <FormattedMessage id="global.remove" defaultMessage="Remove" />
          </Button>
        )}

        <ErrorText error={globalFormError} />
      </form>

      <PatientRemovalConfirmationModal />
    </Modal>
  );
};
